import {useMemo} from 'react';
import type {MbscResource} from '@mobiscroll/react';
import {SectorType} from 'crqlar-react-common/dist/models/sector/sector.model';
import type {Resource} from 'crqlar-react-common/dist/models/resource/resource.model';
import type {Service} from 'crqlar-react-common/dist/models/service/service.model';
import type {Sector} from 'crqlar-react-common/dist/models/sector/sector.model';
import {
    SECTORS_WITH_PERMITTED_UNASSIGNED_ROW,
    UnassignedType
} from 'crqlar-react-common/dist/utils/hooks/reservations/calendar/calendar.util';

interface MbscResourcesParams {
    sector: Sector | undefined
    availableResources: Resource[]
    selectedResources: Resource[]
    childrenResources: Resource[]
    services: Service[]
    mbscResourceColumnType: 'services' | 'resources' | undefined
    usesChildrenResources: boolean
}

type NamedEntity = Pick<Resource, 'id' | 'name'>

const resourceNameCollator = new Intl.Collator(undefined, {
    numeric: true,
    sensitivity: 'base'
})

const compareResourceNames = (left: NamedEntity, right: NamedEntity): number => {
    const nameComparison = resourceNameCollator.compare(left.name ?? '', right.name ?? '')

    return nameComparison !== 0
        ? nameComparison
        : resourceNameCollator.compare(left.id ?? '', right.id ?? '')
}

const sortRestaurantResources = <T extends NamedEntity>(resources: T[], enabled: boolean): T[] => (
    enabled ? [...resources].sort(compareResourceNames) : resources
)

/**
 * Builds the final Mobiscroll resource tree. The array order is the rendered row order, so
 * presentation sorting belongs here instead of in the data query or the calendar component.
 */
export const buildMbscResources = ({
    sector,
    availableResources,
    selectedResources,
    childrenResources,
    services,
    mbscResourceColumnType,
    usesChildrenResources,
}: MbscResourcesParams): MbscResource[] => {
    const resources: MbscResource[] = []

    // Unassigned is a dedicated action row and intentionally remains above alphabetic resources.
    if (SECTORS_WITH_PERMITTED_UNASSIGNED_ROW.includes(sector?.sectorType as SectorType)) {
        const unassignedId = mbscResourceColumnType === 'services'
            ? `${UnassignedType.UNASSIGNED_SERVICE}-${sector?.id}`
            : `${UnassignedType.UNASSIGNED_RESOURCE}-${sector?.id}`

        resources.push({id: unassignedId, name: 'Unassigned', cssClass: 'unassigned-resource-row'})
    }

    if (mbscResourceColumnType === 'services') {
        services.forEach(service => {
            if (service.id) {
                resources.push({
                    id: service.id,
                    name: service.name
                })
            }
        })
        return resources
    }

    const isRestaurant = sector?.sectorType === SectorType.RESTAURANT
    const selectedResourceIds = new Set(selectedResources.map(resource => resource.id))
    const visibleResources = availableResources.filter(resource => selectedResourceIds.has(resource.id))
    const rootResources = sortRestaurantResources(visibleResources, isRestaurant)

    rootResources.forEach(resource => {
        if (!resource.id) return

        const childResources = sortRestaurantResources(
            childrenResources.filter(child => child.parentId === resource.id),
            isRestaurant
        ).map(child => ({
            id: child.id!,
            name: child.name
        }))
        const hasChildResources = childResources.length > 0

        resources.push({
            id: resource.id,
            name: resource.name,
            children: hasChildResources ? childResources : undefined,
            collapsed: hasChildResources ? false : undefined,
            cssClass: hasChildResources && isRestaurant
                ? 'calendar-resource-row--hide-parent-toggle'
                : undefined,
            eventCreation: !usesChildrenResources
        })
    })

    return resources
}

export const useMbscResources = (params: MbscResourcesParams): MbscResource[] => {
    const {
        sector,
        availableResources,
        selectedResources,
        childrenResources,
        services,
        mbscResourceColumnType,
        usesChildrenResources,
    } = params

    return useMemo(
        () => buildMbscResources({
            sector,
            availableResources,
            selectedResources,
            childrenResources,
            services,
            mbscResourceColumnType,
            usesChildrenResources,
        }),
        [
            sector,
            availableResources,
            selectedResources,
            childrenResources,
            services,
            mbscResourceColumnType,
            usesChildrenResources,
        ]
    )
}
